import { ColumnEnum } from '@/enums';

/**
 * Merges the user's saved drag-to-reorder order (localStorage) on top of the
 * canonical default order. Saved columns come first in the user's chosen order;
 * any default column the user never moved is appended in its default position,
 * so newly-added or untouched columns still appear in the PDF.
 */
const mergeWithSavedOrder = (
  storageKey: string,
  defaultOrder: ColumnEnum[],
): ColumnEnum[] => {
  let savedOrder: string[] = [];
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) savedOrder = JSON.parse(stored);
  } catch {
    savedOrder = [];
  }

  const defaultSet = new Set<string>(defaultOrder);
  // Keep only ids that are real data columns (present in the default list).
  const fromSaved = savedOrder.filter((id) => defaultSet.has(id)) as ColumnEnum[];
  const savedSet = new Set<string>(fromSaved);
  const remaining = defaultOrder.filter((id) => !savedSet.has(id));

  return [...fromSaved, ...remaining];
};

const soldDefaultColumnOrder: ColumnEnum[] = [
  ColumnEnum.street,
  ColumnEnum.contractDate,
  ColumnEnum.soldPrice,
  ColumnEnum.livingArea,
  ColumnEnum.rooms,
  ColumnEnum.floorFloors,
  ColumnEnum.constructionYear,
  ColumnEnum.plotArea,
  ColumnEnum.ancillaryArea,
  ColumnEnum.soldPriceSqm,
  ColumnEnum.recountedPrice,
  ColumnEnum.recountedPriceSqm,
  ColumnEnum.taxasionValue,
  ColumnEnum.taxationYear,
  ColumnEnum.priceTaxationRatio,
  ColumnEnum.monthlyFee,
  ColumnEnum.ownSale,
  ColumnEnum.monthlyFeeSqm,
  ColumnEnum.source,
  ColumnEnum.adRegisteredDate,
  ColumnEnum.adDeleteDate,
  ColumnEnum.adAdvertisingDays,
  ColumnEnum.adPrice,
  ColumnEnum.adPriceSqm,
  ColumnEnum.firm,
  ColumnEnum.prospectUrl,
  ColumnEnum.propertyUnitDesignation,
  ColumnEnum.valuationYear,
  ColumnEnum.homeType,
  ColumnEnum.ownershipType,
  ColumnEnum.housingAssociationReportYear,
  ColumnEnum.ownerName,
  ColumnEnum.ownerId,
  ColumnEnum.allabrfUrl,
  ColumnEnum.allabolagUrl,
  ColumnEnum.shoreDistanceType,
  ColumnEnum.waterSewerageType,
  ColumnEnum.alterationYear,
  ColumnEnum.standardPoints,
  ColumnEnum.leaseholdFee,
  ColumnEnum.leaseholdPeriod,
  ColumnEnum.subdivisionType,
  ColumnEnum.balcony,
  ColumnEnum.elevator,
];

export const getSoldTableColumnOrder = () =>
  mergeWithSavedOrder('sold-column-order', soldDefaultColumnOrder);

const adsDefaultColumnOrder: ColumnEnum[] = [
  ColumnEnum.street,
  ColumnEnum.registeredDate,
  ColumnEnum.advertisingDays,
  ColumnEnum.constructionYear,
  ColumnEnum.livingArea,
  ColumnEnum.rooms,
  ColumnEnum.floorFloors,
  ColumnEnum.plotArea,
  ColumnEnum.ancillaryArea,
  ColumnEnum.price,
  ColumnEnum.priceSqm,
  ColumnEnum.monthlyFee,
  ColumnEnum.homeType,
  ColumnEnum.ownershipType,
  ColumnEnum.firm,
  ColumnEnum.pdf,
];

export const getAdsTableColumnOrder = () =>
  mergeWithSavedOrder('ads-column-order', adsDefaultColumnOrder);